import { useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import Column from 'modules/ui/atoms/Layout/Column'
import { FormProvider, useForm } from 'react-hook-form'
import useFlow from 'modules/flow/state/use-flow'
import useOrder from 'modules/order/state/order/use-order'
import useStatus from 'modules/status/state/use-status'
import useConfig from 'modules/config/state/use-config'
import combineSchemas from 'utils/functions/combineSchemas'
import useFinalizeOrder from 'modules/order/finalize-order/use-finalize-order'
import OrderContactQuestions1 from 'modules/order/components/contact-questions/contact-questions-1'
import OrderContactQuestions2 from 'modules/order/components/contact-questions/contact-questions-2'
import FlowTwoColumnStepLayout from 'modules/flow/components/two-column-step-layout/two-column-step-layout'
import { FormAddressValue } from 'modules/order/state/address/address-types'
import { OrderContactSlice } from 'modules/order/state/contact/contact-types'
import { OrderContactAnswersSlice } from 'modules/order/state/contact-answers/contact-answers-types'
import { OrderCustomAnswersSlice } from 'modules/order/state/custom-answers/custom-answers-types'
import OrderAddress from 'modules/order/components/address/address'
import {
  trackFormSubmitEvent,
  trackFormErrorEvent,
} from '../../../analytics/helpers/track-form-events'
import { FormWithoutStyles } from '../shared/form.styled'
import { PrivacyPolicyNotice } from './components/privacy-police-notice'
import { CurrencyDisclaimer } from './components/currency-disclaimer'
import { CARD_DETAILS_STEP_NAME } from 'modules/flow/state/utils/steps/card-details'
import { BANK_DETAILS_STEP_NAME } from 'modules/flow/state/utils/steps/bank-details'
import { StatusType } from 'modules/status/state/status-types'
import { sendDonationChangedMessage, sendDonationCompletedMessage } from 'sdk/utils/sdk-messenger'
import { formatCurrency } from 'utils/functions/currencyUtils'
import { getFrequencyLabel } from 'utils/functions/frequencyUtils'

export type FormData = OrderContactSlice &
  OrderContactAnswersSlice &
  OrderCustomAnswersSlice &
  FormAddressValue

const DonorInformationStep = () => {
  const { t, i18n } = useTranslation()
  const flow = useFlow()
  const order = useOrder()
  const status = useStatus()
  const config = useConfig()
  const finalizeOrder = useFinalizeOrder()

  const isDcfChecked = order.dcf.value.isChecked
  const uncheckedAmountValue = order.amount.value.toFixed(2)
  const checkedAmountValue = ((order.amount.value ?? 0) + order.dcf.value.fee).toFixed(2)

  const formAddress: FormAddressValue = {
    addressLine1: order.address.value.line1,
    addressLine2: order.address.value.line2,
    ...order.address.value,
  }

  const form = useForm<FormData>({
    values: {
      ...order.contact.value,
      ...order.contactAnswers.value,
      ...formAddress,
    },
    resolver: yupResolver(
      combineSchemas([order.contact.schema, order.contactAnswers.schema, order.address.schema]),
    ),
  })

  const goNext = () => {
    flow.nextStep && flow.goToStep(flow.nextStep.name)
  }

  const goToSelectedPaymentTypeStep = () => {
    if (order.paymentDetails.type === 'CC') {
      flow.goToStep(CARD_DETAILS_STEP_NAME)

      status.set(
        StatusType.FAILED,
        t('DonorInformationStep.status.ccError', 'Please provide valid Credit Card details'),
      )
    } else if (order.paymentDetails.type === 'ACH') {
      flow.goToStep(BANK_DETAILS_STEP_NAME)

      status.set(
        StatusType.FAILED,
        t('DonorInformationStep.status.achError', 'Please provide valid ACH details'),
      )
    }
  }

  const watchFirstName = form.watch('firstName')
  const watchLastName = form.watch('lastName')
  const watchEmail = form.watch('email')
  const watchPhone = form.watch('phone')
  const watchZip = form.watch('zipCode')
  const watchStreet = form.watch('addressLine1')
  const watchCity = form.watch('city')
  const watchState = form.watch('state').toUpperCase()
  const watchCountry = form.watch('country').toUpperCase()

  useEffect(() => {
    sendDonationChangedMessage(config.campaignId, {
      // donation
      amount: order.amount.value,
      frequency: order.frequency.value,
      currency: order.currency.value,
      // contact
      first: watchFirstName,
      last: watchLastName,
      email: watchEmail,
      phone: watchPhone,
      // address
      street: watchStreet,
      city: watchCity,
      state: watchState,
      zip: watchZip,
      country: watchCountry,
    })
  }, [
    order,
    config,
    watchFirstName,
    watchLastName,
    watchEmail,
    watchPhone,
    watchZip,
    watchStreet,
    watchCity,
    watchState,
    watchCountry,
  ])

  const onSubmit = async (data: FormData) => {
    // Track form submission event
    trackFormSubmitEvent(flow.currentStep.name)

    order.contact.setValue({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: data.phone,
      optIn: data.optIn,
      optInSms: data.optInSms,
      isNameAnonymous: data.isNameAnonymous,
      isAmountAnonymous: data.isAmountAnonymous,
      isCompanyCard: data.isCompanyCard,
      cardCompanyName: data.cardCompanyName,
    })

    order.contactAnswers.setValue({
      middle_name: data.middle_name,
      website: data.website,
      suffix: data.suffix,
      company_name: data.company_name,
      gender: data.gender,
      pronoun: data.pronoun,
      prefix: data.prefix,
      birthdate: data.birthdate,
      cellphone: data.cellphone,
    })

    order.address.setValue({
      city: data.city,
      state: data.state,
      country: data.country,
      zipCode: data.zipCode,
      line1: data.addressLine1,
      line2: data.addressLine2,
    })

    try {
      await finalizeOrder()
      goNext()
      sendDonationCompletedMessage()
    } catch (error: unknown) {
      goToSelectedPaymentTypeStep()
    }
  }

  const handleFormErrors = () => {
    const firstErrorField = Object.values(form.formState.errors)[0]

    if (firstErrorField && firstErrorField.message) {
      trackFormErrorEvent(flow.currentStep.name, firstErrorField.message)
    }
  }

  return (
    <FormProvider {...form}>
      <FormWithoutStyles onSubmit={form.handleSubmit(onSubmit, handleFormErrors)}>
        <FlowTwoColumnStepLayout.Header
          title={flow.currentStep.title}
          backButtonDataId="donor-information-back-button"
        />
        <FlowTwoColumnStepLayout.Body>
          <Column>
            <OrderContactQuestions1 />
            <OrderAddress
              errors={form.formState.errors}
              watchCountry={watchCountry.toUpperCase()}
              watchState={watchState.toUpperCase()}
            />
            <OrderContactQuestions2 showRequired />
            <CurrencyDisclaimer />
          </Column>
        </FlowTwoColumnStepLayout.Body>
        <FlowTwoColumnStepLayout.Footer>
          <FlowTwoColumnStepLayout.FooterText>
            <PrivacyPolicyNotice />
          </FlowTwoColumnStepLayout.FooterText>
          <FlowTwoColumnStepLayout.FooterButton type="submit" dataId="donate-button">
            {t('DonorInformationStep.footerButton.donateMessage.now', 'Donate Now')}
          </FlowTwoColumnStepLayout.FooterButton>
        </FlowTwoColumnStepLayout.Footer>
      </FormWithoutStyles>
    </FormProvider>
  )
}

export default DonorInformationStep
